// shared-types/src/serviceBilling/billingSchedule.ts — contract term vs payment frequency math
import { BillingInterval, PaymentFrequency } from './types'

export function contractTermMonths(contract: BillingInterval): number {
    switch (contract) {
        case BillingInterval.MONTHLY:
            return 1
        case BillingInterval.SEMIANNUAL:
            return 6
        case BillingInterval.ANNUAL:
            return 12
        default:
            return 1
    }
}

export function paymentFrequencyMonths(freq: PaymentFrequency): number {
    switch (freq) {
        case PaymentFrequency.MONTHLY:
            return 1
        case PaymentFrequency.QUARTERLY:
            return 3
        case PaymentFrequency.SEMIANNUAL:
            return 6
        case PaymentFrequency.ANNUAL:
            return 12
        default:
            return 1
    }
}

/**
 * Number of subscription charges in one full contract term, or null if incompatible
 * (e.g. quarterly billing on a 1-month contract).
 */
export function subscriptionInstallmentCount(
    contract: BillingInterval,
    payment: PaymentFrequency
): number | null {
    const cm = contractTermMonths(contract)
    const pm = paymentFrequencyMonths(payment)
    if (pm > cm) return null
    if (cm % pm !== 0) return null
    return cm / pm
}

export function resolveEffectivePaymentFrequency(
    contract: BillingInterval,
    paymentFrequency: PaymentFrequency | null | undefined
): PaymentFrequency {
    if (paymentFrequency != null) return paymentFrequency
    return contract as unknown as PaymentFrequency
}

/** Per-charge amount from the contract-period total (catalog or custom). */
export function subscriptionAmountPerInstallment(
    contractTotalAmount: number,
    contract: BillingInterval,
    payment: PaymentFrequency
): number | null {
    const n = subscriptionInstallmentCount(contract, payment)
    if (n == null || n <= 0) return null
    return Math.round((contractTotalAmount / n) * 100) / 100
}

/** IA credits to grant per subscription charge (proportional to installments). */
export function aiCreditsPerSubscriptionInstallment(
    creditsIncludedPerContract: number,
    contract: BillingInterval,
    payment: PaymentFrequency
): number {
    const n = subscriptionInstallmentCount(contract, payment)
    if (n == null || n <= 0) return creditsIncludedPerContract
    return Math.round(creditsIncludedPerContract / n)
}
